/**
 * Load EXIF sidecars for a set of original keys.
 *
 * Each photo's metadata lives at `<originalKey>.json` (written on upload).
 * We fetch sidecars via presigned GET URLs and cache them in a React Query
 * map. Missing sidecars (older photos, or upload races) resolve to undefined
 * and simply don't contribute to the map/timeline.
 */
import { useQuery } from '@tanstack/react-query';
import pLimit from 'p-limit';
import { resolveSignedUrl } from './useSignedUrl.ts';
import { toSidecarKey } from '../lib/keys.ts';
import type { ExifData } from '../lib/types.ts';

const limit = pLimit(6);

async function fetchOneSidecar(originalKey: string): Promise<ExifData | null> {
  try {
    const url = await resolveSignedUrl(toSidecarKey(originalKey));
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = (await res.json()) as ExifData;
    return data && typeof data === 'object' ? data : null;
  } catch {
    return null;
  }
}

/**
 * Fetch EXIF for a SINGLE key (e.g. the photo open in the lightbox).
 * This is the cheap path for the gallery — we avoid fetching EXIF for every
 * thumbnail (hundreds of calls) and only pay for what the user actually opens.
 */
export function useExifOne(originalKey: string | null) {
  return useQuery({
    queryKey: ['exif-one', originalKey],
    enabled: !!originalKey,
    staleTime: 5 * 60_000,
    queryFn: () => fetchOneSidecar(originalKey as string),
  });
}

/**
 * Fetch EXIF for many keys, returning a Map keyed by original key.
 * Keys with no sidecar are simply absent from the map.
 */
export function useExifBatch(keys: string[], enabled = true) {
  // Stable query key from the sorted set of keys.
  const queryKey = ['exif', keys.length, keys.length ? keys[0] : '', keys.length ? keys[keys.length - 1] : ''];

  return useQuery({
    queryKey,
    enabled: enabled && keys.length > 0,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const entries = await Promise.all(
        keys.map((key) =>
          limit(async () => {
            const exif = await fetchOneSidecar(key);
            return [key, exif] as const;
          }),
        ),
      );
      const map = new Map<string, ExifData>();
      for (const [key, exif] of entries) {
        if (exif) map.set(key, exif);
      }
      return map;
    },
  });
}
